import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { AppArtBucket } from '../types';

interface BucketState {
  count: number;
  hasPrimary: boolean;
}

type Buckets = Partial<Record<AppArtBucket, BucketState>>;

interface ArtFilesContextValue {
  buckets: Buckets;
  report: (bucket: AppArtBucket, state: BucketState) => void;
}

const ArtFilesContext = createContext<ArtFilesContextValue>({
  buckets: {},
  report: () => undefined,
});

/**
 * Estado AGORA de cada bucket: quantos arquivos e se algum está marcado com ★.
 *
 * A galeria mantém a lista em estado local (recarregar o record resetaria o
 * form e apagaria o briefing não salvo), então `record.files` envelhece assim
 * que alguém sobe ou marca um arquivo. A toolbar precisa do dado vivo para
 * saber se já dá para avançar de status — daí este contexto no lugar do record.
 */
export const ArtFilesProvider = ({ children }: { children: ReactNode }) => {
  const [buckets, setBuckets] = useState<Buckets>({});

  const report = useCallback((bucket: AppArtBucket, state: BucketState) => {
    setBuckets(current => {
      const previous = current[bucket];
      if (previous && previous.count === state.count && previous.hasPrimary === state.hasPrimary) {
        return current;
      }
      return { ...current, [bucket]: state };
    });
  }, []);

  const value = useMemo(() => ({ buckets, report }), [buckets, report]);

  return <ArtFilesContext.Provider value={value}>{children}</ArtFilesContext.Provider>;
};

export const useArtFiles = () => useContext(ArtFilesContext);

export default ArtFilesContext;
